/**
 * StickyAktenkopfzeile — Feste Kopfzeile für alle Akten-Prozessschritte
 * Issue #123 — bleibt beim Scrollen oben kleben, ist aufklappbar.
 *
 * Kompakt-Zeile (immer sichtbar):
 *   [▾] AUFTRAGSTYP (Gutachten) · Status · Aufgaben-Zähler · KFZ · Schaden · Vers. · Badges
 * Ausgeklappt: Aufgaben-Modul · Pipeline · Finanzen · Beteiligte · Frist/Aktion.
 *
 * Aktenzeichen + Name stehen in der Haupt-Kopfzeile darüber (AkteDetail).
 * Neutrales SaaS-Farbschema (ein Indigo-Akzent), keine betriebsbunten Farben.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { ChevronDown, ChevronUp, Phone, Calendar, Target, Sparkles, Car, Loader2, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { PruefungBadge } from '@/components/akte/PruefungBadge';
import { PruefungDialog } from '@/components/akte/PruefungDialog';
import { AktenProgressbar } from '@/components/akte/AktenProgressbar';
import { AktenSubStatusBadges } from '@/components/akte/AktenSubStatusBadges';
import { PhaseSetzenDialog } from '@/components/akte/PhaseSetzenDialog';
import SvDokumentErstellen from '@/components/akte/SvDokumentErstellen';
import AkteAufgabenCard from '@/components/akte/AkteAufgabenCard';
import { useAufgabenCRUD } from '@/hooks/useAufgabenCRUD';
import { STATUS_LABELS, STATUS_COLORS, supabase } from '@/lib/supabase';
import { type BetriebId } from '@/lib/betriebe';
import { istSvVerfahren } from '@/lib/navigation';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import type { Akte } from '@/lib/database.types';
import {
  stellungnahmeStatusLabel,
  stellungnahmeStatusChipClasses,
  berechneSchriftsatzfristBadge,
  schriftsatzfristBadgeClasses,
  schriftsatzfristBadgeText,
} from '@/lib/akten-stellungnahme';
import {
  StellungnahmeBadge,
  StellungnahmeToggle,
  hasStellungnahmeLabel,
} from '@/components/akte/StellungnahmeLabel';
import { ForderungStagePill } from '@/components/forderung/ForderungStagePill';
import { AKTE_PHASEN, isGutachtenOnly, resolvePhaseId } from '@/lib/akte-phasen';

// Neutraler SaaS-Akzent (Indigo) — ersetzt die betriebsspezifischen Farben.
const ACCENT = '#4f46e5';

export type PipelinePhase = {
  id: string;
  label: string;
};

const NAECHSTE_AKTIONEN = [
  'Gutachten erstellen',
  'Stellungnahme schreiben',
  'Rechnung stellen',
  'Kunde anrufen',
  'Anwalt informieren',
  'Versicherung anschreiben',
  'Besichtigung terminieren',
  'Werkstatt kontaktieren',
  'Dokumente zusammenstellen',
  'Mahnung senden',
] as const;

/**
 * Auftragstypen für Gutachten-Akten (gespeichert in akten.auftragstyp_sv).
 * Der Wert 'sachverstaendigenverfahren' schaltet die linke Prozess-Navi auf die
 * SV-Verfahren-Schrittfolge (siehe lib/navigation.ts).
 */
const AUFTRAGSTYP_SV_OPTIONS: { value: string; label: string }[] = [
  { value: 'haftpflicht', label: 'Haftpflichtgutachten' },
  { value: 'kasko', label: 'Kaskoschadengutachten' },
  { value: 'kostenvoranschlag', label: 'Kostenvoranschlag' },
  { value: 'reparaturkostenprognose', label: 'Reparaturkostenprognose' },
  { value: 'wertgutachten', label: 'Wertgutachten' },
  { value: 'fahrzeugbewertung', label: 'Fahrzeugbewertung' },
  { value: 'zustandsbericht', label: 'Zustandsbericht' },
  { value: 'sachverstaendigenverfahren', label: 'Sachverständigenverfahren' },
];

const STORAGE_KEY = 'sticky_aktenkopfzeile_v1';

type HeaderState = {
  frist?: string;
  naechsteAktion?: string;
  phaseId?: string;
};

function loadHeaderState(): Record<string, HeaderState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function saveHeaderState(data: Record<string, HeaderState>) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function formatMoney(value: number | null | undefined): string | null {
  if (value == null) return null;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function KompaktFeld({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-1 min-w-0">
      <span className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-[12px] font-semibold text-gray-800 truncate ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}

function DetailFeld({
  label,
  value,
  phone,
  money,
}: {
  label: string;
  value: string | null | undefined;
  phone?: string | null;
  money?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`text-[12px] font-semibold text-gray-800 truncate ${money ? 'tabular-nums' : ''}`}>
          {value}
        </span>
        {phone && (
          <a
            href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
            className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
            title={`${phone} anrufen`}
            data-testid="sticky-phone-link"
          >
            <Phone className="w-3 h-3 text-gray-500" />
          </a>
        )}
      </div>
    </div>
  );
}

export default function StickyAktenkopfzeile({
  akte,
  betriebId,
  stellungnahmenCount,
  onToggleStellungnahme,
  onRefetch,
}: {
  akte: Akte;
  betriebId: BetriebId;
  stellungnahmenCount?: number;
  onToggleStellungnahme?: (active: boolean) => void | Promise<void>;
  onRefetch?: () => void;
}) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [state, setState] = useState<Record<string, HeaderState>>(loadHeaderState);
  const [pruefungDialogOpen, setPruefungDialogOpen] = useState(false);
  const [phaseDialogZiel, setPhaseDialogZiel] = useState<number | null>(null);
  const [savingTyp, setSavingTyp] = useState(false);
  const akteHasStellungnahme = hasStellungnahmeLabel(akte.labels);
  const [, navigate] = useLocation();

  // Offene Aufgaben dieser Akte (für den Zähler in der Kompakt-Zeile).
  const { aufgaben } = useAufgabenCRUD();
  const offeneAufgaben = useMemo(
    () => aufgaben.filter(a => a.akte_id === akte.id && a.status !== 'erledigt' && a.status !== 'abgebrochen').length,
    [aufgaben, akte.id],
  );

  // Pre-fetch linked Mietwagen-Akte identifier for header icon navigation (Issue #273).
  const mietwagenAkteId: string | null = akte.mietwagen_akte_id ?? null;
  const [mietwagenAkteIdentifier, setMietwagenAkteIdentifier] = useState<string | null>(null);
  const lastFetchedMietwagenId = useRef<string | null>(null);

  useEffect(() => {
    if (!mietwagenAkteId || betriebId !== 'gutachten') return;
    if (lastFetchedMietwagenId.current === mietwagenAkteId) return;
    lastFetchedMietwagenId.current = mietwagenAkteId;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('akten')
          .select('akten_nummer, akten_nr')
          .eq('id', mietwagenAkteId)
          .maybeSingle();
        if (!cancelled && data) {
          setMietwagenAkteIdentifier(aktenIdentifier(data));
        }
      } catch { /* non-critical — icon just won't be navigatable */ }
    })();
    return () => { cancelled = true; };
  }, [mietwagenAkteId, betriebId]);

  const aktenKey = akte.akten_nr || akte.id;
  const current = state[aktenKey] || {};

  const update = (patch: Partial<HeaderState>) => {
    const next = { ...state, [aktenKey]: { ...current, ...patch } };
    setState(next);
    saveHeaderState(next);
  };

  const auftragstypSv = (akte as any).auftragstyp_sv as string | null | undefined;
  const setAuftragstyp = async (value: string) => {
    setSavingTyp(true);
    try {
      const { error } = await supabase.from('akten').update({ auftragstyp_sv: value }).eq('id', akte.id);
      if (error) throw error;
      const label = AUFTRAGSTYP_SV_OPTIONS.find(o => o.value === value)?.label ?? value;
      toast.success(`Auftragstyp: ${label} — Prozess-Navi angepasst`);
      onRefetch?.();
    } catch (e: any) {
      toast.error('Auftragstyp konnte nicht gespeichert werden: ' + (e?.message ?? e));
    } finally {
      setSavingTyp(false);
    }
  };

  const statusLabel = STATUS_LABELS[akte.status as keyof typeof STATUS_LABELS] || akte.status;
  const statusColor = STATUS_COLORS[akte.status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-600';
  const kennzeichen = akte.kennzeichen || akte.fahrzeug_kennzeichen || null;
  const kunde = akte.kunde_name || akte.titel || '';
  const istSv = betriebId === 'gutachten' && istSvVerfahren(auftragstypSv);

  const pipelinePhasen = AKTE_PHASEN[betriebId] || AKTE_PHASEN.gutachten;
  const resolvedPhaseId = resolvePhaseId(betriebId, current.phaseId);
  const aktivePhaseIdx = Math.max(
    0,
    pipelinePhasen.findIndex(p => p.id === resolvedPhaseId),
  );

  const repNettoStr = formatMoney(akte.reparaturkosten_netto);
  const repBruttoStr = formatMoney(akte.reparaturkosten_brutto);
  const schadensumme = repNettoStr && repBruttoStr
    ? `${repNettoStr} / ${repBruttoStr}`
    : repBruttoStr || repNettoStr;

  return (
    <div
      data-testid="sticky-aktenkopfzeile"
      className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-200 flex-shrink-0"
    >
      {/* ─── Kompakt-Zeile (immer sichtbar) ─── */}
      <div className="px-3 sm:px-4 lg:px-5 py-2 flex items-center gap-2 sm:gap-3 overflow-x-auto">
        <button
          type="button"
          data-testid="sticky-header-toggle"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition-colors flex-shrink-0"
          title={expanded ? 'Kopfzeile einklappen' : 'Kopfzeile ausklappen'}
          aria-expanded={expanded}
        >
          {expanded
            ? <ChevronUp   className="w-4 h-4 text-gray-500" />
            : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </button>

        {/* Auftragstyp-Selektor (prominent) — nur Gutachten */}
        {betriebId === 'gutachten' && (
          <label
            className="flex items-center gap-1.5 flex-shrink-0"
            title="Auftragstyp — bestimmt den Prozess-Ablauf (z. B. Sachverständigenverfahren)"
            data-testid="sticky-auftragstyp"
          >
            <select
              value={auftragstypSv || ''}
              disabled={savingTyp}
              onChange={e => setAuftragstyp(e.target.value)}
              className={`text-[12px] font-semibold rounded-md px-2 py-1 bg-white border focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-60 ${auftragstypSv ? 'border-gray-300 text-gray-800' : 'border-amber-400 text-amber-700'}`}
            >
              <option value="">— Auftragstyp wählen —</option>
              {AUFTRAGSTYP_SV_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {savingTyp && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
          </label>
        )}

        {/* Aufgaben-Zähler — öffnet den ausgeklappten Bereich mit dem Aufgaben-Modul */}
        <button
          type="button"
          data-testid="sticky-aufgaben-count"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0"
          title="Aufgaben dieser Akte anzeigen"
        >
          <CheckSquare className="w-3 h-3" />
          Aufgaben
          {offeneAufgaben > 0 && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-indigo-600 text-white text-[10px]">
              {offeneAufgaben}
            </span>
          )}
        </button>

        {/* Stellungnahme-Status-Chip (Issue #413) — nur für Gutachten */}
        {isGutachtenOnly(betriebId) && (() => {
          const stnLabel = stellungnahmeStatusLabel(akte.stellungnahme_status);
          const stnChipClasses = stellungnahmeStatusChipClasses(akte.stellungnahme_status);
          if (!stnLabel || !stnChipClasses) return null;
          const fristBadge = berechneSchriftsatzfristBadge(akte.schriftsatzfrist);
          return (
            <div className="flex items-center gap-1 flex-shrink-0" data-testid="sticky-stellungnahme-chip">
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${stnChipClasses}`}
                data-stellungnahme-status={akte.stellungnahme_status ?? ''}
              >
                Stellungnahme: {stnLabel}
              </span>
              {fristBadge && fristBadge.level !== 'normal' && (
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${schriftsatzfristBadgeClasses(fristBadge.level)}`}
                  data-testid="sticky-schriftsatzfrist-badge"
                  data-schriftsatzfrist-level={fristBadge.level}
                >
                  {schriftsatzfristBadgeText(fristBadge)}
                </span>
              )}
            </div>
          );
        })()}

        {/* Prüfungs-QS-Badge (Issue #463) — nur wenn pruefung_status != 'keine' */}
        {akte.pruefung_status && akte.pruefung_status !== 'keine' && (
          <PruefungBadge
            status={akte.pruefung_status}
            onClick={() => setPruefungDialogOpen(true)}
          />
        )}

        {/* Stellungnahmen-Anzeige (Issue #240) — nur für Gutachten */}
        {isGutachtenOnly(betriebId) && typeof stellungnahmenCount === 'number' && stellungnahmenCount > 0 && (
          <span data-testid="sticky-stellungnahmen-count">
            <StellungnahmeBadge count={stellungnahmenCount} />
          </span>
        )}

        {/* ─── Rechts: Stammdaten kompakt + Aktionen ─── */}
        <div className="ml-auto flex items-center gap-3 flex-shrink-0">
          {/* Betriebsspezifisch */}
          {betriebId === 'autoglas'      && akte.glasart         && <KompaktFeld label="Glas"      value={akte.glasart} />}
          {betriebId === 'mietwagen'     && akte.mietbeginn      && <KompaktFeld label="Miete ab"  value={akte.mietbeginn.slice(0, 10)} />}
          {betriebId === 'werkstatt'     && akte.auftragsart     && <KompaktFeld label="Auftrag"   value={akte.auftragsart} />}
          {betriebId === 'unfallgiganten'&& akte.vermittelt_durch&& <KompaktFeld label="Vermittelt"value={akte.vermittelt_durch} />}

          {/* SV-Verfahren: Dokument als PDF erzeugen + E-Mail vorbereiten */}
          {istSv && <SvDokumentErstellen akte={akte as any} onRefetch={onRefetch} />}

          {/* Mietwagen-Verknüpfungs-Icon (Issue #272/#273) — neutral */}
          {betriebId === 'gutachten' && !!mietwagenAkteId && (
            <button
              type="button"
              data-testid="sticky-mietwagen-linked-icon"
              title={mietwagenAkteIdentifier ? `Zur Mietwagen-Akte ${mietwagenAkteIdentifier}` : 'Mit Mietwagen-Akte verknüpft'}
              disabled={!mietwagenAkteIdentifier}
              onClick={() => {
                if (mietwagenAkteIdentifier) {
                  navigate(buildAkteHref('mietwagen', mietwagenAkteIdentifier));
                }
              }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-gray-600 flex-shrink-0 hover:bg-gray-200 transition-colors disabled:cursor-default"
            >
              <Car className="w-3 h-3" />
              <span className="text-[10px] font-semibold">Mietwagen</span>
            </button>
          )}

          {/* Forderungs-Stage-Pill (Issue #598) */}
          {akte.forderung_stage != null && (
            <ForderungStagePill
              stage={akte.forderung_stage}
              forderungsAkteId={akte.forderungsakte_id}
            />
          )}
        </div>
      </div>

      {/* ─── Ausgeklappter Bereich ─── */}
      {expanded && (
        <div className="px-3 sm:px-4 lg:px-5 pb-3 border-t border-gray-100 space-y-3">
          {/* Aufgaben-Modul direkt im Header (alle Betriebe) */}
          <div className="pt-3">
            <AkteAufgabenCard
              akteId={akte.id}
              businessUnit={betriebId}
              akteLabel={`${akte.akten_nr ?? ''}${kunde ? ' · ' + kunde : ''}`}
            />
          </div>

          {/* Hauptphasen-Progressbar (Issue #415, #744) */}
          <div className="pt-1 flex flex-col gap-2">
            <AktenProgressbar
              hauptphase={akte.hauptphase}
              hauptphase_datum={akte.hauptphase_datum}
              betriebId={betriebId}
              onPhaseClick={(phase) => setPhaseDialogZiel(phase)}
            />
            <AktenSubStatusBadges
              zahlungsstatus={akte.zahlungsstatus}
              klage_status={akte.klage_status}
              warten_auf={akte.warten_auf}
              stellungnahme_status={akte.stellungnahme_status}
              betriebId={betriebId}
            />
          </div>

          {/* Labels-Zeile — nur für Gutachten (Issue #744) */}
          {isGutachtenOnly(betriebId) && onToggleStellungnahme && (
            <div className="pt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-gray-400 uppercase tracking-wide">Labels</span>
              <StellungnahmeToggle
                active={akteHasStellungnahme}
                onToggle={onToggleStellungnahme}
                data-testid="sticky-stellungnahme-toggle-expanded"
              />
              {typeof stellungnahmenCount === 'number' && stellungnahmenCount > 0 && (
                <StellungnahmeBadge count={stellungnahmenCount} />
              )}
            </div>
          )}

          {/* Finanzen */}
          <div className="pt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
            {schadensumme && <DetailFeld label="Schadensumme" value={schadensumme} money />}
            <DetailFeld label="Wiederbeschaffungswert" value={formatMoney(akte.wiederbeschaffungswert)} money />
            <DetailFeld label="Minderwert" value={formatMoney(akte.minderwert)} money />
            <DetailFeld label="Restwert"   value={formatMoney(akte.restwert)}   money />
          </div>

          {/* Beteiligte */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2">
            <DetailFeld
              label="Sachverständiger"
              value={akte.gutachten_sv || akte.erteilt_durch}
            />
            <DetailFeld
              label="Anwalt"
              value={akte.anwalt_name}
              phone={akte.anwalt_telefon}
            />
            <DetailFeld
              label="Werkstatt"
              value={akte.werkstatt_name}
              phone={akte.werkstatt_telefon}
            />
          </div>

          {/* Frist & Nächste Aktion */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-[11px] text-gray-500 uppercase tracking-wide flex-shrink-0">Frist</span>
              <input
                type="date"
                data-testid="sticky-frist-input"
                value={current.frist || ''}
                onChange={e => update({ frist: e.target.value })}
                className="flex-1 min-w-0 border border-gray-200 rounded px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <label className="flex items-center gap-2">
              <Target className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-[11px] text-gray-500 uppercase tracking-wide flex-shrink-0">Nächste Aktion</span>
              <select
                data-testid="sticky-naechste-aktion"
                value={current.naechsteAktion || ''}
                onChange={e => update({ naechsteAktion: e.target.value })}
                className="flex-1 min-w-0 border border-gray-200 rounded px-2 py-1 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
              >
                <option value="">— wählen —</option>
                {NAECHSTE_AKTIONEN.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              <Sparkles
                className="w-3.5 h-3.5 text-gray-300 flex-shrink-0"
                aria-label="KI-Vorschlag folgt in einem späteren PR"
              />
            </label>
          </div>

          {/* Fall-Status-Pipeline */}
          <div
            className="flex items-center gap-1 overflow-x-auto"
            data-testid="sticky-pipeline"
            role="tablist"
            aria-label="Fall-Status-Pipeline"
          >
            {pipelinePhasen.map((phase, idx) => {
              const isActive = resolvedPhaseId === phase.id;
              const isPast   = resolvedPhaseId ? idx < aktivePhaseIdx : false;
              return (
                <button
                  key={phase.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => update({ phaseId: phase.id })}
                  className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors flex-shrink-0 ${
                    isActive
                      ? 'text-white border-transparent'
                      : isPast
                        ? 'bg-gray-100 text-gray-600 border-gray-200'
                        : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                  style={isActive ? { backgroundColor: ACCENT } : undefined}
                >
                  {phase.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Prüfungs-QS-Dialog (Issue #463) */}
      {pruefungDialogOpen && (
        <PruefungDialog
          akteId={akte.id}
          aktuellerStatus={akte.pruefung_status}
          pruefung_zugewiesen_an={akte.pruefung_zugewiesen_an}
          open={pruefungDialogOpen}
          onClose={() => setPruefungDialogOpen(false)}
        />
      )}

      {/* Phase-Setzen-Dialog (Issue #415, #744) */}
      {phaseDialogZiel !== null && (
        <PhaseSetzenDialog
          akteId={akte.id}
          aktuellePhase={Math.min(Math.max(Number(akte.hauptphase ?? 1), 1), (AKTE_PHASEN[betriebId] ?? AKTE_PHASEN.gutachten).length)}
          zielPhase={phaseDialogZiel}
          betriebId={betriebId}
          open={phaseDialogZiel !== null}
          onClose={() => setPhaseDialogZiel(null)}
          onSuccess={() => { setPhaseDialogZiel(null); onRefetch?.(); }}
        />
      )}
    </div>
  );
}
